
import React, { useState } from 'react';
import ReportMap from '@/components/map/ReportMap';
import ReportDetails from '@/components/ReportDetails';
import { Button } from '@/components/ui/button';
import { CategoryFilter } from './CategoryFilter';
import { SortControls } from './SortControls';
import { Map, List, Navigation, Loader2 } from 'lucide-react';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useToast } from '@/components/ui/use-toast';

interface ReportMapViewProps {
  reports: any[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  sortBy: "date" | "supports";
  sortOrder: "asc" | "desc";
  onSortChange: (sortBy: "date" | "supports", sortOrder: "asc" | "desc") => void;
}

export const ReportMapView: React.FC<ReportMapViewProps> = ({
  reports,
  selectedCategory,
  onCategoryChange,
  sortBy,
  sortOrder,
  onSortChange
}) => {
  const [selectedReport, setSelectedReport] = useState<any | null>(null);
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  const { position, loading, error, requestLocation } = useGeolocation();
  const { toast } = useToast();

  const handleReportClick = (report: any) => {
    setSelectedReport(report);
  };

  const handleLocateMe = () => {
    requestLocation();
    if (error) {
      toast({
        title: "Erreur de géolocalisation",
        description: error,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Localisation en cours",
        description: "Recherche de votre position...",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Carte des signalements</h2>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'map' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('map')}
              className="flex items-center gap-1"
            >
              <Map className="h-4 w-4" />
              Carte
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="flex items-center gap-1"
            >
              <List className="h-4 w-4" />
              Liste
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLocateMe}
              className="flex items-center gap-1 ml-2"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Navigation className="h-4 w-4" />
              )}
              Ma position
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <CategoryFilter
            selectedCategory={selectedCategory}
            onCategoryChange={onCategoryChange}
          />
          <SortControls
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={onSortChange}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg overflow-hidden shadow-sm">
        <ReportMap 
          reports={reports} 
          onReportClick={handleReportClick}
          centerLat={position?.latitude}
          centerLng={position?.longitude}
        />
      </div>

      {selectedReport && (
        <ReportDetails
          report={selectedReport}
          isOpen={!!selectedReport}
          onClose={() => setSelectedReport(null)}
          onSupport={() => {}}
        />
      )}
    </div>
  );
};
